import { useState } from 'react';
import type { ComplianceSummary, Organization } from '@/types';
import { ic } from '@/components/Icons';
import { DataTable, Pagination, PageHeader, ProgressBar, type Column } from '@/components/ui';
import { HoverCard, HoverRow, InitialsBadge } from '@/components/ui/HoverCard';
import { COUNTRIES, ds } from '@/data';
import { useI18n } from '@/i18n';
import { progresoOrg } from '@/lib/progresoPortal';
import { paginate, sortRows, useTableState, type SortMap } from '@/hooks/useTableState';
import { useGo } from '@/hooks/useRoute';

type OrgRow = Organization & {
  _key: string;
  _sites: number;
  _instr: number;
  _compl: ComplianceSummary | null;
};

/** Compact summary of the most relevant organization data, on hover. */
function OrgNameCell({ r }: { r: OrgRow }) {
  const { lang, t } = useI18n();
  const country = COUNTRIES.find((c) => c.code === r.OrgCountry)?.[lang === 'es' ? 'name_es' : 'name_en'] || r.OrgCountry;
  // el porcentaje del socio: su ficha, sus sites, sus instructores y sus
  // documentos de compliance, no solo los campos de esta tabla
  const c = progresoOrg(r.OrgId);
  const addr = [r.RegisteredAddress1, r.RegisteredCity, r.RegisteredStateProvince].filter(Boolean).join(', ');

  return (
    <HoverCard
      trigger={
        <>
          <div style={{ fontWeight: 600 }}>{r.OrgName}</div>
          <div className="mono" style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 1 }}>{r.OrgId}</div>
        </>
      }
    >
      {() => (
        <>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
            <InitialsBadge name={r.OrgName} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--brand-ink)', letterSpacing: '-0.01em' }}>{r.OrgName}</div>
              <div className="mono" style={{ fontSize: 10.5, color: 'var(--text-3)', marginTop: 2 }}>{r.OrgId}</div>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, paddingTop: 10, borderTop: '1px solid var(--line)' }}>
            <HoverRow label={lang === 'es' ? 'País' : 'Country'}>{country}</HoverRow>
            <HoverRow label={lang === 'es' ? 'Dirección' : 'Address'}>{addr || '—'}</HoverRow>
            <HoverRow label={lang === 'es' ? 'Ingreso' : 'Joined'}>{r.YearJoined}</HoverRow>
            <HoverRow label={lang === 'es' ? 'Sitio web' : 'Website'} bad={!r.OrgWebAddress}>
              {r.OrgWebAddress || (lang === 'es' ? 'Sin dato' : 'Missing')}
            </HoverRow>
            <HoverRow label="Sites">{r._sites}</HoverRow>
            <HoverRow label={lang === 'es' ? 'Instructores' : 'Instructors'}>{r._instr}</HoverRow>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--line)' }}>
            <span style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 600, flexShrink: 0 }}>{t('common.completeness')}</span>
            <div style={{ flex: 1 }}><ProgressBar pct={c.pct} /></div>
            <span className="mono" style={{ fontSize: 12, fontWeight: 700 }}>{c.pct}%</span>
          </div>
        </>
      )}
    </HoverCard>
  );
}

export interface OrgListProps {
  /** Restricts the list to a partner manager's own organizations. */
  orgIds?: string[];
  title?: string;
  sub?: string;
}

export function OrgList({ orgIds, title, sub }: OrgListProps) {
  const { lang, t } = useI18n();
  const go = useGo();
  const [q, setQ] = useState('');
  const [country, setCountry] = useState('');
  const table = useTableState(12);

  const countryName = (code: string) => {
    const c = COUNTRIES.find((x) => x.code === code);
    return c ? (lang === 'es' ? c.name_es : c.name_en) : code || '';
  };

  let all = ds.listOrgs({ q });
  if (orgIds) all = all.filter((o) => orgIds.includes(o.OrgId));

  const countryOptions = Array.from(new Set(all.map((o) => o.OrgCountry).filter(Boolean)))
    .map((c) => ({ code: c, name: countryName(c) }))
    .sort((a, b) => a.name.localeCompare(b.name, 'es'));
  if (country) all = all.filter((o) => o.OrgCountry === country);

  // enrich every match first, so sorting spans all pages
  const enriched: OrgRow[] = all.map((o) => ({
    ...o,
    _key: o.OrgId,
    _sites: ds.listSites({ orgId: o.OrgId }).length,
    _instr: ds.listAssignments({ orgId: o.OrgId }).length,
    _compl: ds.getCompliance(o.OrgId),
  }));

  const sortValues: SortMap<OrgRow> = {
    OrgName: { type: 'text', get: (r) => r.OrgName },
    country: { type: 'text', get: (r) => countryName(r.OrgCountry) },
    _sites: { type: 'number', get: (r) => r._sites },
    _instr: { type: 'number', get: (r) => r._instr },
    YearJoined: { type: 'number', get: (r) => Number(r.YearJoined) || 0 },
    web: { type: 'number', get: (r) => (r.OrgWebAddress ? 1 : 0) },
    compliance: { type: 'number', get: (r) => (r._compl ? r._compl.pct : -1) },
  };

  const { rows, page, pageCount } = paginate(sortRows(enriched, table.sort, sortValues), table.page, table.per);

  const columns: Column<OrgRow>[] = [
    { key: 'OrgName', label: lang === 'es' ? 'Organización' : 'Organization', sortable: true, render: (r) => <OrgNameCell r={r} /> },
    { key: 'country', label: lang === 'es' ? 'País' : 'Country', sortable: true, render: (r) => countryName(r.OrgCountry) },
    { key: '_sites', label: 'Sites', align: 'right', mono: true, sortable: true },
    { key: '_instr', label: lang === 'es' ? 'Instr.' : 'Instr.', align: 'right', mono: true, sortable: true },
    { key: 'YearJoined', label: lang === 'es' ? 'Año' : 'Year', align: 'right', mono: true, sortable: true },
    {
      key: 'web',
      label: 'Web',
      sortable: true,
      render: (r) => (r.OrgWebAddress ? <span className="chip ok">{ic.check} OK</span> : <span className="chip warn">—</span>),
    },
    {
      key: 'compliance',
      label: 'Compliance',
      sortable: true,
      render: (r) =>
        !r._compl ? (
          <span className="chip" style={{ color: 'var(--text-3)' }} data-tip={lang === 'es' ? 'Sin documentos de compliance' : 'No compliance documents'}>
            —
          </span>
        ) : (
          <div
            data-tip={
              lang === 'es'
                ? `${r._compl.count} documento${r._compl.count === 1 ? '' : 's'} · ${r._compl.verified} verificado${r._compl.verified === 1 ? '' : 's'}`
                : `${r._compl.count} document${r._compl.count === 1 ? '' : 's'} · ${r._compl.verified} verified`
            }
            style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 110 }}
          >
            <div style={{ flex: 1 }}><ProgressBar pct={r._compl.pct} /></div>
            <span className="mono" style={{ fontSize: 11, fontWeight: 700, minWidth: 34, textAlign: 'right', color: r._compl.pct === 100 ? 'var(--ok)' : 'var(--text-2)' }}>
              {r._compl.pct}%
            </span>
          </div>
        ),
    },
    { key: 'go', label: '', align: 'right', render: () => <span style={{ color: 'var(--text-3)' }}>{ic.chevR}</span> },
  ];

  return (
    <div className="screen-enter" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <PageHeader
        title={title || t('nav.orgs')}
        sub={sub != null ? sub : lang === 'es' ? `${all.length} organizaciones en la base` : `${all.length} organizations in the database`}
        actions={
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <select
              value={country}
              onChange={(e) => {
                setCountry(e.target.value);
                table.setPage(1);
              }}
              aria-label={lang === 'es' ? 'Filtrar por país' : 'Filter by country'}
              style={{ padding: '7px 10px', borderRadius: 7, border: '1px solid var(--line-2)', fontSize: 12, background: 'var(--surface)', maxWidth: 180 }}
            >
              <option value="">{lang === 'es' ? 'Todos los países' : 'All countries'}</option>
              {countryOptions.map((c) => (
                <option key={c.code} value={c.code}>{c.name}</option>
              ))}
            </select>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)' }}>{ic.search}</span>
              <input
                placeholder={t('common.search')}
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  table.setPage(1);
                }}
                style={{ padding: '8px 12px 8px 32px', border: '1px solid var(--line-2)', borderRadius: 7, fontSize: 13, width: 240 }}
              />
            </div>
          </div>
        }
      />
      <DataTable
        columns={columns}
        rows={rows}
        sort={table.sort}
        onSort={table.onSort}
        stickyHeader
        onRowClick={(o) => go('org', { orgId: o.OrgId })}
      />
      <Pagination page={page} pageCount={pageCount} onPage={table.setPage} total={all.length} per={table.per} onPer={table.onPer} perOptions={[12, 25, 50, 100]} />
    </div>
  );
}
